/**
 * 게시물 관련 리듀서
 */
import {
  SET_POSTS,
  ADD_POST,
  UPDATE_POST,
  DELETE_POST,
  LIKE_POST,
  UNLIKE_POST,
  ADD_COMMENT,
  DELETE_COMMENT
} from '../actions/actionTypes.js';

// 초기 상태
const initialState = {
  posts: [],
  userPosts: {},
  currentPost: null,
  hasMore: true,
  lastPostTime: null
};

/**
 * 게시물 리듀서 함수
 * @param {Object} state - 현재 상태
 * @param {Object} action - 액션 객체
 * @returns {Object} 새로운 상태
 */
export const postsReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_POSTS:
      return {
        ...state,
        posts: action.payload,
        hasMore: action.payload.length > 0,
        lastPostTime: action.payload.length > 0 ? action.payload[action.payload.length - 1].createdAt : null
      };
    
    case ADD_POST:
      return {
        ...state,
        posts: [action.payload, ...state.posts]
      };
    
    case UPDATE_POST:
      return {
        ...state,
        posts: state.posts.map(post => 
          post.id === action.payload.postId 
            ? { ...post, ...action.payload.updateData } 
            : post
        ),
        currentPost: state.currentPost && state.currentPost.id === action.payload.postId 
          ? { ...state.currentPost, ...action.payload.updateData } 
          : state.currentPost
      };
    
    case DELETE_POST:
      return {
        ...state,
        posts: state.posts.filter(post => post.id !== action.payload),
        currentPost: state.currentPost && state.currentPost.id === action.payload ? null : state.currentPost
      };
    
    case LIKE_POST:
      return {
        ...state,
        posts: state.posts.map(post => {
          if (post.id === action.payload.postId) {
            // likes 데이터 처리 - 객체를 배열로 변환
            let likes = post.likes || [];
            if (!Array.isArray(likes) && typeof likes === 'object') {
              likes = Object.values(likes);
            }

            // userId가 문자열이거나 객체인 경우 모두 처리
            const userExists = likes.some(like => 
              (typeof like === 'string' && like === action.payload.userId) || 
              (typeof like === 'object' && like.userId === action.payload.userId)
            );

            if (!userExists) {
              // 사용자 좋아요 정보 추가
              likes.push({ 
                userId: action.payload.userId,
                createdAt: new Date().toISOString()
              });
            }

            return {
              ...post,
              likes: likes,
              likeCount: likes.length
            };
          }
          return post;
        }),
        currentPost: state.currentPost && state.currentPost.id === action.payload.postId 
          ? {
              ...state.currentPost,
              likes: (() => {
                let likes = state.currentPost.likes || [];
                if (!Array.isArray(likes) && typeof likes === 'object') {
                  likes = Object.values(likes);
                }
                
                const userExists = likes.some(like => 
                  (typeof like === 'string' && like === action.payload.userId) || 
                  (typeof like === 'object' && like.userId === action.payload.userId)
                );
                
                if (!userExists) {
                  likes.push({ 
                    userId: action.payload.userId,
                    createdAt: new Date().toISOString()
                  });
                }
                
                return likes;
              })(),
              likeCount: (() => {
                let likes = state.currentPost.likes || [];
                if (!Array.isArray(likes) && typeof likes === 'object') {
                  likes = Object.values(likes);
                }
                
                const userExists = likes.some(like => 
                  (typeof like === 'string' && like === action.payload.userId) || 
                  (typeof like === 'object' && like.userId === action.payload.userId)
                );
                
                return userExists ? likes.length : likes.length + 1;
              })()
            } 
          : state.currentPost
      };
    
    case UNLIKE_POST:
      return {
        ...state,
        posts: state.posts.map(post => {
          if (post.id === action.payload.postId) {
            // likes 데이터 처리 - 객체를 배열로 변환
            let likes = post.likes || [];
            if (!Array.isArray(likes) && typeof likes === 'object') {
              likes = Object.values(likes);
            }
            
            // userId 필터링 - 문자열 또는 객체 형태 모두 처리
            const newLikes = likes.filter(like => {
              if (typeof like === 'string') {
                return like !== action.payload.userId;
              } else if (typeof like === 'object') {
                return like.userId !== action.payload.userId;
              }
              return true;
            });
            
            return {
              ...post,
              likes: newLikes,
              likeCount: newLikes.length
            };
          }
          return post;
        }),
        currentPost: state.currentPost && state.currentPost.id === action.payload.postId 
          ? {
              ...state.currentPost,
              likes: (() => {
                let likes = state.currentPost.likes || [];
                if (!Array.isArray(likes) && typeof likes === 'object') {
                  likes = Object.values(likes);
                }
                
                return likes.filter(like => {
                  if (typeof like === 'string') {
                    return like !== action.payload.userId;
                  } else if (typeof like === 'object') {
                    return like.userId !== action.payload.userId;
                  }
                  return true;
                });
              })(),
              likeCount: (() => {
                let likes = state.currentPost.likes || [];
                if (!Array.isArray(likes) && typeof likes === 'object') {
                  likes = Object.values(likes);
                }
                
                const userExists = likes.some(like => 
                  (typeof like === 'string' && like === action.payload.userId) || 
                  (typeof like === 'object' && like.userId === action.payload.userId)
                );
                
                return userExists ? Math.max(0, likes.length - 1) : likes.length;
              })()
            } 
          : state.currentPost
      };
    
    case ADD_COMMENT:
      return {
        ...state,
        posts: state.posts.map(post => {
          if (post.id === action.payload.postId) {
            // comments 데이터 처리 - 객체를 배열로 변환
            let comments = post.comments || [];
            if (!Array.isArray(comments) && typeof comments === 'object') {
              comments = Object.values(comments);
            }
            
            // 새 댓글 추가
            const newComments = [...comments, action.payload.comment];
            
            return {
              ...post,
              comments: newComments,
              commentCount: newComments.length
            };
          }
          return post;
        }),
        currentPost: state.currentPost && state.currentPost.id === action.payload.postId 
          ? {
              ...state.currentPost,
              comments: (() => {
                let comments = state.currentPost.comments || [];
                if (!Array.isArray(comments) && typeof comments === 'object') {
                  comments = Object.values(comments);
                }
                return [...comments, action.payload.comment];
              })(),
              commentCount: (() => {
                let comments = state.currentPost.comments || [];
                if (!Array.isArray(comments) && typeof comments === 'object') {
                  comments = Object.values(comments);
                }
                return comments.length + 1;
              })()
            } 
          : state.currentPost
      };
    
    case DELETE_COMMENT:
      return {
        ...state,
        posts: state.posts.map(post => {
          if (post.id === action.payload.postId) {
            // comments 데이터 처리 - 객체를 배열로 변환
            let comments = post.comments || [];
            if (!Array.isArray(comments) && typeof comments === 'object') {
              comments = Object.values(comments);
            }
            
            // 댓글 ID 필터링
            const newComments = comments.filter(comment => 
              (typeof comment === 'object' && comment.id !== action.payload.commentId)
            );
            
            return {
              ...post,
              comments: newComments,
              commentCount: newComments.length
            };
          }
          return post;
        }),
        currentPost: state.currentPost && state.currentPost.id === action.payload.postId 
          ? {
              ...state.currentPost,
              comments: (() => {
                let comments = state.currentPost.comments || [];
                if (!Array.isArray(comments) && typeof comments === 'object') {
                  comments = Object.values(comments);
                }
                return comments.filter(comment => 
                  (typeof comment === 'object' && comment.id !== action.payload.commentId)
                );
              })(),
              commentCount: (() => {
                let comments = state.currentPost.comments || [];
                if (!Array.isArray(comments) && typeof comments === 'object') {
                  comments = Object.values(comments);
                }
                
                const commentExists = comments.some(comment => 
                  (typeof comment === 'object' && comment.id === action.payload.commentId)
                );
                
                return commentExists ? Math.max(0, comments.length - 1) : comments.length;
              })()
            } 
          : state.currentPost
      };
    
    default:
      return state;
  }
}; 